/** @jsx jsx */
import { jsx, Link, Label, Box } from '@front10/base-elements';
import { useSelect } from 'downshift';
import * as React from 'react';
import { FilterButton } from './FilterButton';
import { Input } from './Input';
import { useRect } from '@reach/rect';
import { useIsMobileView, useTranslation } from '@front10/utils';
import nanoid from 'nanoid';
import { isSSR } from '@front10/utils';
import VisuallyHidden from '@reach/visually-hidden';
import { useButton } from '@react-aria/button';
import { OverlayContainer, useOverlayPosition, useOverlayTrigger } from '@react-aria/overlays';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { Dropdown } from './DateRangePicker/Popover';

interface Props {
  label: string;
  prefixSelectedItem?: string;
  ariaLabelledBy?: string;
  ariaLabelDescription?: string;
  placeholder?: string;
  disabled?: boolean;
  defaultValue?: string;
  options: Option[];
  name?: string;
  portal?: boolean;
  onChange?: (value: string) => void;
  onClick?: () => void;
  showLabel?: boolean;
  layout?: 'compact' | 'standard' | 'closed';
  labelNotSelectedItem?: string;
  forcePlaceholder?: boolean;
  loading?: boolean;
  focusWhenIsShow?: boolean;
  filterButtonIconProps?: $TSFixMe;
  filterButtonIconLeftProps?: $TSFixMe;
  activeFilterButton?: boolean;
  renderItem?: (
    item: $TSFixMe,
    itemsProps: $TSFixMe,
    itemSelected: $TSFixMe,
    itemHighlighted: $TSFixMe
  ) => void;
  dataTestIdButton?: string;
  dataTestIdPopover?: string;
  className?: string;
  filterContentRef?: React.RefObject<HTMLDivElement>;
}

interface Option {
  value: string;
  label: React.ReactNode;
  href?: string;
  testId?: string;
}

const styles = {
  menu: {
    overflowY: 'auto',
    padding: 0,
    margin: 0,
    listStyle: 'none',
    position: 'relative'
  }
} as const;

const KEY_ARROW_DOWN = 40;

const ListboxCmp = (props: Props, ref: any) => {
  const items = props.options;
  const [id] = React.useState(nanoid());
  const translate = useTranslation();
  const layout = props.layout ?? 'compact';
  const showLabel = props.showLabel ?? layout !== 'compact';
  const defaultSelectedItem = React.useMemo(
    () => props.options.find((elem) => elem.value === props.defaultValue),
    [props.defaultValue, props.options]
  );
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const rect = useRect(triggerRef);

  const stateReducer = (state: any, actionAndTypeChanges: any) => {
    const { type, changes } = actionAndTypeChanges;

    // Keep selected item highlighted when opening the menu, instead of moving to the next item
    if (
      type === useSelect.stateChangeTypes.ToggleButtonKeyDownArrowDown ||
      type === useSelect.stateChangeTypes.ToggleButtonKeyDownArrowUp ||
      type === useSelect.stateChangeTypes.ToggleButtonClick ||
      type === useSelect.stateChangeTypes.FunctionOpenMenu ||
      type === useSelect.stateChangeTypes.FunctionToggleMenu
    ) {
      if (!state.isOpen && changes.isOpen) {
        const selectedIndex = items.findIndex(
          (elem) => elem.value === (state.selectedItem ? state.selectedItem.value : undefined)
        );
        return {
          ...changes,
          highlightedIndex: selectedIndex >= 0 ? selectedIndex : 0
        };
      }
    }
    if (type === useSelect.stateChangeTypes.MenuMouseLeave) {
      return {
        ...changes,
        highlightedIndex: state.highlightedIndex
      };
    }
    if (type === useSelect.stateChangeTypes.MenuKeyDownEscape) {
      overlayState.close();
      closeMenu();
      return {
        ...changes,
        highlightedIndex: state.highlightedIndex
      };
    }
    if (
      type === useSelect.stateChangeTypes.MenuKeyDownEnter ||
      type === useSelect.stateChangeTypes.MenuKeyDownSpaceButton
    ) {
      closeMenu();
      overlayState.close();
      selectItem(changes.selectedItem);
      return {
        ...changes,
        highlightedIndex: state.highlightedIndex
      };
    }
    return { ...changes };
  };

  const {
    selectedItem,
    selectItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    closeMenu
  } = useSelect({
    id: id,
    items,
    selectedItem: defaultSelectedItem,
    stateReducer,
    onStateChange: () => props.onClick?.(),
    onSelectedItemChange: (event) => {
      props.onChange?.(event.selectedItem?.value || '');
      overlayState.close();
      const element: any = document?.querySelector(`#listBoxId-${id}`);
      if (element) {
        element.focus();
      }
    },
    itemToString: (item) => (item ? String(item.label) : '')
  });

  const isMounted = React.useRef(false);
  const isMobile = useIsMobileView();
  let overlayState = useOverlayTriggerState({});

  React.useEffect(() => {
    const newFocusWhenIsShow = props.focusWhenIsShow === undefined ? true : props.focusWhenIsShow;
    const ac = new AbortController();
    if (!overlayState.isOpen && !props.loading && !isMounted.current) {
      if (newFocusWhenIsShow) {
        triggerRef.current?.focus();
        isMounted.current = true;
      }
    }
    return () => {
      // Abort both fetches on unmount
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, overlayState.isOpen, props.loading]);

  const improveIdToggleButtonProps = (data: any) => {
    const baseId = data.id;
    return {
      ...data,
      id: `${baseId}-${id}`,
      'aria-labelledby': data['aria-labelledby'].replace(baseId, `${baseId}-${id}`),
      className: props?.className
    };
  };
  const improveIdlabelProps = (data: any) => {
    const baseId = data.id;
    return {
      ...data,
      id: `${baseId}-${id}`
    };
  };
  const improveId = (data: any, baseId: any, newId: any) => {
    return {
      ...data,
      'aria-labelledby': data['aria-labelledby'].replace(baseId, `${newId}`)
    };
  };

  const privateTargetRef: React.RefObject<HTMLDivElement> = React.useRef(null);

  let toggleButtonProps = improveIdToggleButtonProps(getToggleButtonProps({ ref: triggerRef }));
  let labelProps = getLabelProps();
  const oldId = labelProps.id;
  labelProps = improveIdlabelProps(labelProps);
  toggleButtonProps = improveId(toggleButtonProps, oldId, labelProps.id);
  let menuProps = improveId(
    getMenuProps({
      'aria-activedescendant': `${id}-item-${highlightedIndex}`,
      role: 'listbox',
      style: styles.menu,
      onBlur: () => {
        overlayState.close();
      }
    }),
    oldId,
    labelProps.id
  );
  menuProps['aria-labelledby'] = `listBoxId-${id}`;
  React.useImperativeHandle(
    ref,
    () => ({
      focusToggleElement: () => {
        if (!isSSR()) {
          const element: any = document?.querySelector(`#listBoxId-${id}`);
          if (element) {
            element.focus();
          }
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  React.useEffect(() => {
    const closeOnScroll = () => {
      closeMenu();
      overlayState.close();
    };

    const element = document.querySelector('div[aria-label="Room 1"]');

    element?.parentElement?.addEventListener('scroll', closeOnScroll);
    window?.addEventListener('scroll', closeOnScroll);

    return () => {
      element?.parentElement?.removeEventListener('scroll', closeOnScroll);
      window?.removeEventListener('scroll', closeOnScroll);
    };
  }, [closeMenu, isMobile, overlayState]);

  // Scroll highlighted item into view when navigating with arrow keys
  React.useEffect(() => {
    if (overlayState.isOpen && highlightedIndex >= 0) {
      const highlightedElement = document.querySelector(`#${id}-item-${highlightedIndex}`);
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }, [highlightedIndex, overlayState.isOpen, id]);

  // console.log('triggerProps', triggerProps);
  // useButton ensures that focus management is handled correctly,
  // across all browsers. Focus is restored to the button once the
  // popover closes.
  let { buttonProps } = useButton(
    {
      onPress: (e) => {
        overlayState.open();
      },
      elementType: 'div'
    },
    triggerRef
  );

  // Get popover positioning props relative to the trigger
  let { overlayProps: positionProps, updatePosition } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef: privateTargetRef,
    placement: 'bottom left',
    offset: 0,
    isOpen: overlayState.isOpen
  });

  let overlayTriggerState1 = { ...overlayState, close: updatePosition };
  let { triggerProps } = useOverlayTrigger({ type: 'dialog' }, overlayTriggerState1, triggerRef);

  const triggerButton = (
    <FilterButton
      {...toggleButtonProps}
      iconLeftSettings={props?.filterButtonIconLeftProps}
      iconSettings={props?.filterButtonIconProps}
      active={
        props?.activeFilterButton !== undefined
          ? props?.activeFilterButton
          : !!selectedItem?.value && layout === 'compact'
      }
      value={
        (!props.forcePlaceholder &&
          `${
            props.prefixSelectedItem !== null && props.prefixSelectedItem !== undefined
              ? props.prefixSelectedItem + ' ' + selectedItem?.label
              : selectedItem?.label
              ? selectedItem?.label
              : props.label
          }`) ||
        props.placeholder ||
        props.labelNotSelectedItem ||
        translate('cmp-listbox-not-selected-item', 'Elements')
      }
      layout={layout}
      aria-labelledby={
        props.ariaLabelledBy !== undefined
          ? `${toggleButtonProps['aria-labelledby']} ${props.ariaLabelledBy}`
          : showLabel
          ? toggleButtonProps['aria-labelledby']
          : null
      }
      aria-label={`${
        selectedItem?.label
          ? `${translate('cmp-travel-class-filter-label-selected-item', 'Select')} ${props.label} ${
              selectedItem?.label
            }`
          : showLabel
          ? undefined
          : props.label
      } `}
      onClick={overlayState.open}
      onKeyDown={(evt) => {
        if (evt.keyCode === KEY_ARROW_DOWN) {
          evt.preventDefault();
          overlayState.open();
        }
        toggleButtonProps?.onKeyDown?.(evt);
      }}
      disabled={props.disabled}
      id={`listBoxId-${id}`}
      aria-haspopup="listbox"
      aria-controls={overlayState.isOpen ? menuProps.id : null}
      data-testid={props.dataTestIdButton}
    />
  );

  const newToggleElement = React.cloneElement(triggerButton, {
    ...buttonProps,
    ...triggerProps,
    ...triggerButton.props,
    ref: triggerRef,
    componentReference: triggerRef
  });

  const newPositionProps = {
    style: {
      ...positionProps.style,
      zIndex: 99999999999,
      minWidth: rect?.width,
      maxWidth: isMobile ? rect?.width : '100%',
      background: 'transparent'
    }
  };

  const dropdown = (
    <React.Fragment>
      <VisuallyHidden aria-live="polite">
        {overlayState.isOpen ? translate('cmp-listbox-filter-expanded', 'Filter expanded') : ''}
      </VisuallyHidden>
      {newToggleElement}
      <OverlayContainer>
        {overlayState.isOpen ? (
          <Dropdown
            {...newPositionProps}
            popoverRef={privateTargetRef}
            onClose={overlayState.close}
          >
            <Box
              sx={{
                variant: 'layout.listbox',
                marginTop: 0
              }}
            >
              <ul
                {...menuProps}
                sx={{
                  '@media (max-height: 320px)': {
                    height: '35dvh'
                  },
                  variant: 'menus.default'
                }}
              >
                {items.map((item, index) => {
                  const itemSelected = selectedItem && selectedItem.value === items[index].value;
                  const itemHighlighted = highlightedIndex === index;
                  if (typeof props.renderItem === 'function') {
                    return props.renderItem(
                      item,
                      { ...getItemProps({ item, index, 'aria-selected': itemSelected ?? false }) },
                      itemSelected,
                      itemHighlighted
                    );
                  } else {
                    return (
                      <li
                        data-item-selected={itemSelected}
                        data-item-highlighted={itemHighlighted}
                        sx={{
                          variant: 'menuItems.default',
                          cursor: item.href ? 'cursor' : 'default'
                        }}
                        key={`${item.value}-list`}
                        id={`${id}-item-${index}`}
                        {...getItemProps({ item, index, 'aria-selected': itemSelected ?? false })}
                        data-testid={item.testId ? item.testId : null}
                      >
                        {item.href ? (
                          <Link key={item.value} href={item.href}>
                            {item.label}
                          </Link>
                        ) : (
                          item.label
                        )}
                      </li>
                    );
                  }
                })}
              </ul>
            </Box>
          </Dropdown>
        ) : null}
      </OverlayContainer>
    </React.Fragment>
  );

  if (layout === 'closed') {
    return <Input label={props.label}>{dropdown}</Input>;
  }

  const renderLabel = showLabel ? (
    <Label
      variant="text.fontSize6"
      color="text"
      mb="2"
      {...{ ...labelProps, htmlFor: `${toggleButtonProps.id}` }}
    >
      {props.label}
    </Label>
  ) : null;
  return (
    <div sx={{ position: 'relative' }}>
      {renderLabel}
      {dropdown}
    </div>
  );
};

const Listbox = React.forwardRef(ListboxCmp);

export { Listbox };
